import type { Taster } from '../types'
import { isTasterComplete } from './scoring'

export type TasterPair = {
  tasterA: string
  tasterB: string
  correlation: number
}

export type CorrelationResult = {
  matrix: Map<string, Map<string, number>> // tasterA -> tasterB -> correlation
  closestPair: TasterPair | null
  mostDifferentPair: TasterPair | null
}

/**
 * Calculate Spearman's rank correlation coefficient
 * ρ = 1 - (6 × Σd²) / (n × (n² - 1))
 */
function spearmanCorrelation(scoresA: number[], scoresB: number[]): number {
  const n = scoresA.length
  if (n < 2) return 0

  let sumDSquared = 0
  for (let i = 0; i < n; i++) {
    const d = scoresA[i] - scoresB[i]
    sumDSquared += d * d
  }

  return 1 - (6 * sumDSquared) / (n * (n * n - 1))
}

export function calculateCorrelations(tasters: Taster[]): CorrelationResult {
  const completeTasters = tasters.filter(isTasterComplete)
  const matrix = new Map<string, Map<string, number>>()

  let closestPair: TasterPair | null = null
  let mostDifferentPair: TasterPair | null = null

  for (let i = 0; i < completeTasters.length; i++) {
    const tasterA = completeTasters[i]
    const innerMap = new Map<string, number>()
    matrix.set(tasterA.name, innerMap)

    for (let j = 0; j < completeTasters.length; j++) {
      if (i === j) continue

      const tasterB = completeTasters[j]
      const correlation = spearmanCorrelation(
        tasterA.scores as number[],
        tasterB.scores as number[],
      )
      innerMap.set(tasterB.name, correlation)

      // Only check pairs once (i < j) to avoid duplicates
      if (i < j) {
        const pair: TasterPair = {
          tasterA: tasterA.name,
          tasterB: tasterB.name,
          correlation,
        }

        if (closestPair === null || correlation > closestPair.correlation) {
          closestPair = pair
        }
        if (mostDifferentPair === null || correlation < mostDifferentPair.correlation) {
          mostDifferentPair = pair
        }
      }
    }
  }

  return { matrix, closestPair, mostDifferentPair }
}
